import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ArrowLeft, BookOpen, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const STATUS_CLS = {
  POSTED: 'bg-emerald-100 text-emerald-700',
  REVERSED: 'bg-amber-100 text-amber-800',
  DRAFT: 'bg-gray-100 text-gray-700',
};

const JournalVoucherView = () => {
  const navigate = useNavigate();
  const { document_number, company_code_id, fiscal_year } = useParams();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [jv, setJv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reversing, setReversing] = useState(false);

  // eslint-disable-next-line
  useEffect(() => { load(); }, [document_number, company_code_id, fiscal_year]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${backendUrl}/api/fi/journal-vouchers/${document_number}/${company_code_id}/${fiscal_year}`
      );
      setJv(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load journal voucher');
    } finally { setLoading(false); }
  };

  const handleReverse = async () => {
    if (!window.confirm(`Reverse document ${document_number}? A new offsetting posting will be created.`)) return;
    try {
      setReversing(true);
      const r = await axios.post(
        `${backendUrl}/api/fi/journal-vouchers/${document_number}/${company_code_id}/${fiscal_year}/reverse`
      );
      toast.success(`Reversed. Reversal document: ${r.data.reversal_document_number}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reversal failed');
    } finally {
      setReversing(false);
    }
  };

  if (loading) {
    return <Layout><div className="p-6 text-center text-gray-500">Loading…</div></Layout>;
  }
  if (!jv) {
    return <Layout><div className="p-6 text-center text-red-500">Not found</div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="jv-view-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/finance/journal-vouchers')}>
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BookOpen className="w-7 h-7 text-orange-600" />
                JV {jv.document_number}
              </h1>
              <p className="text-gray-600 mt-1">
                {jv.company_code_id} / {jv.fiscal_year} •{' '}
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLS[jv.status]}`}>
                  {jv.status}
                </span>
              </p>
            </div>
          </div>
          {jv.status === 'POSTED' && (
            <Button variant="outline" onClick={handleReverse} disabled={reversing} data-testid="jv-reverse-btn">
              <RotateCcw className="w-4 h-4 mr-1" />{reversing ? 'Reversing…' : 'Reverse'}
            </Button>
          )}
        </div>

        <Card className="mb-4">
          <CardHeader><CardTitle>Header</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-4 gap-4 text-sm">
            <div><div className="text-gray-500 text-xs">Document Type</div><div className="font-medium">{jv.document_type_id}</div></div>
            <div><div className="text-gray-500 text-xs">Posting Date</div><div className="font-medium">{jv.posting_date}</div></div>
            <div><div className="text-gray-500 text-xs">Document Date</div><div className="font-medium">{jv.document_date}</div></div>
            <div><div className="text-gray-500 text-xs">Currency</div><div className="font-medium">{jv.currency}</div></div>
            <div><div className="text-gray-500 text-xs">Reference</div><div className="font-medium">{jv.reference || '-'}</div></div>
            <div className="col-span-3"><div className="text-gray-500 text-xs">Header Text</div><div className="font-medium">{jv.header_text || '-'}</div></div>
            <div><div className="text-gray-500 text-xs">Posted By</div><div className="font-medium">{jv.posted_by || '-'}</div></div>
            <div><div className="text-gray-500 text-xs">Posted At</div><div className="font-medium text-xs">{jv.posted_at?.slice(0, 19) || '-'}</div></div>
            {jv.status === 'REVERSED' && (
              <>
                <div><div className="text-gray-500 text-xs">Reversed By</div><div className="font-medium">{jv.reversed_by || '-'}</div></div>
                <div>
                  <div className="text-gray-500 text-xs">Reversal Doc</div>
                  <Button
                    variant="link"
                    className="p-0 h-auto"
                    onClick={() => navigate(`/finance/journal-vouchers/${jv.reversal_document_number}/${jv.company_code_id}/${jv.fiscal_year}`)}
                    data-testid="jv-reversal-link"
                  >
                    {jv.reversal_document_number}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader><CardTitle>Line Items</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>D/C</TableHead>
                  <TableHead>PK</TableHead>
                  <TableHead>GL Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Cost Center</TableHead>
                  <TableHead>Profit Center</TableHead>
                  <TableHead>BA</TableHead>
                  <TableHead>Segment</TableHead>
                  <TableHead>Text</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jv.line_items.map((l) => (
                  <TableRow key={l.line_no}>
                    <TableCell className="font-mono">{l.line_no}</TableCell>
                    <TableCell>
                      <Badge variant={l.debit_credit === 'D' ? 'default' : 'secondary'}>
                        {l.debit_credit === 'D' ? 'Dr' : 'Cr'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{l.posting_key}</TableCell>
                    <TableCell>{l.gl_account}</TableCell>
                    <TableCell className="text-right font-mono">
                      {Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>{l.cost_center_id || '-'}</TableCell>
                    <TableCell>{l.profit_center_id || '-'}</TableCell>
                    <TableCell>{l.business_area || '-'}</TableCell>
                    <TableCell>{l.segment_id || '-'}</TableCell>
                    <TableCell className="text-xs">{l.line_text || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase">Total Debit</div>
              <div className="text-2xl font-mono font-semibold">
                {Number(jv.total_debit).toLocaleString(undefined, { minimumFractionDigits: 2 })} {jv.currency}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Total Credit</div>
              <div className="text-2xl font-mono font-semibold">
                {Number(jv.total_credit).toLocaleString(undefined, { minimumFractionDigits: 2 })} {jv.currency}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Balance</div>
              <div className="text-2xl font-mono font-semibold text-emerald-600">✓ Balanced</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default JournalVoucherView;
